import type { TFunction } from "i18next";
import type { Permission, User } from "../../types/types";
import type {
  AttributeValue,
  ObjectReferenceResult,
  PIMProduct,
} from "../../types/types.PIM";
import { endpoints } from "../../endpoints";
import { getObjectReferenceDetailPath } from "./ObjectReferencePreviewTooltip";
import { removeUnderscore, toTitleCase } from "../../util/util";
import type {
  ObjectReferenceArchiveImpact,
  RelatedEntitiesResponse,
} from "../../types/types.MDM";
import { getReferencePermissionSet } from "../../pages/admin/SellerAdmin/MDM/shared/utils";
import axios from "axios";

type PreviewAttributeLike = {
  id?: string | null;
  display_name?: string | null;
  name?: string | null;
  input_type?: string | null;
  source_object_type?: string | null;
  target_object_type?: string | null;
};

export type ObjectReferencePathOptions = {
  isPortfolio: boolean;
  storePath: string;
  adminPath: string;
};

type ObjectReferenceAccessOptions = {
  isPortfolio: boolean;
  user: User | null | undefined;
  roleIsSellerAdmin: boolean;
};

export type ReferenceDisplayAccessContext = ObjectReferenceAccessOptions & {
  hasPermission?: (permission: Permission) => boolean;
  targetObjectType?: string;
};

export type ProductLikeReferenceAccessFields = {
  status?: string | null;
  is_visible_on_storefronts?: boolean;
  is_accessible?: boolean;
  visibility_tenants?: { id: string }[];
  is_viewable?: boolean;
  pim_product_type?: PIMProduct["pim_product_type"] | null;
};

/** Shared storefront / team visibility rules for product_reference and object_ref products. */
export function isProductLikeReferenceDisplayAccessible(
  ref: ProductLikeReferenceAccessFields,
  {
    isPortfolio,
    user,
    roleIsSellerAdmin,
  }: Pick<
    ObjectReferenceAccessOptions,
    "isPortfolio" | "user" | "roleIsSellerAdmin"
  >
): boolean {
  if (roleIsSellerAdmin) {
    return true;
  }

  if (!user) {
    return ref.status === "published" && Boolean(ref.is_visible_on_storefronts);
  }

  const tenantHasAccess = user.tenant_id
    ? (ref.visibility_tenants?.map((t) => t.id) ?? []).includes(user.tenant_id)
    : false;

  if (isPortfolio) {
    if (ref.status !== "published") {
      return false;
    }
    return Boolean(
      ref.is_visible_on_storefronts || ref.is_accessible || tenantHasAccess
    );
  }

  return Boolean(ref.is_viewable);
}

export const isObjectReferenceInputType = (
  inputType?: string | null
): boolean => inputType === "object_reference";

export const shouldShowObjectReferenceAttribute = ({
  inputType,
  objectReferenceEnabled,
}: {
  inputType?: string | null;
  objectReferenceEnabled: boolean;
}): boolean => !isObjectReferenceInputType(inputType) || objectReferenceEnabled;

export const getPreviewAttributeLabel = (
  attribute: PreviewAttributeLike
): string => {
  const name = attribute.display_name ?? attribute.name ?? "";
  const inputType = attribute.input_type
    ? toTitleCase(removeUnderscore(attribute.input_type as string))
    : "Field";
  return `${name} | ${inputType}`;
};

const OBJECT_REFERENCE_DEFAULT_PREVIEW_NAMES: Record<string, string[]> = {
  product: ["Product Name", "Product ID", "Product ERP ID", "Brand Name"],
  supplier: ["Company Name", "Supplier ID", "ERP ID"],
  customer: ["Company Name", "Customer ID", "ERP ID"],
  distributor: ["Company Name", "Distributor ID", "ERP ID"],
};

const OBJECT_REFERENCE_REQUIRED_PREVIEW_NAMES: Record<string, string> = {
  product: "Product Name",
  supplier: "Company Name",
  customer: "Company Name",
  distributor: "Company Name",
};

export const getRequiredPreviewAttributeName = (
  objectType?: string
): string | undefined =>
  objectType ? OBJECT_REFERENCE_REQUIRED_PREVIEW_NAMES[objectType] : undefined;

export const OBJECT_REFERENCE_CONFIRMATION_PENDING =
  "OBJECT_REFERENCE_CONFIRMATION_PENDING";

export function getEntityArchiveOrDeleteWarningMessage(
  t: TFunction,
  {
    pendingEntityAction,
    hasHierarchyImpact,
  }: {
    pendingEntityAction: "archived" | "deleted" | null;
    hasHierarchyImpact: boolean;
  }
): string {
  if (pendingEntityAction === "deleted") {
    return t(
      "This profile has established relationships with other objects. Deleting this profile will unlink these objects. Please confirm before proceeding."
    );
  }
  if (hasHierarchyImpact) {
    return t(
      "This profile has established relationships with other objects. Archiving this profile will unlink these objects, including any parent/subsidiary links in Company Hierarchy. Please confirm before proceeding."
    );
  }
  return t(
    "This profile has established relationships with other objects. Archiving this profile will unlink these objects. Please confirm before proceeding."
  );
}

export function getProductArchiveWarningMessage(t: TFunction): string {
  return t(
    "This product has established relationships with other objects. Archiving this product will unlink these objects. Please confirm before proceeding."
  );
}

export function getObjectReferenceUnlinkConfirmMessage(t: TFunction): string {
  return t(
    "There are established relationships between objects. Saving these changes will unlink these objects. Please confirm before proceeding."
  );
}

export const requestObjectReferenceActionConfirmation = async <
  Action extends string
>({
  action,
  objectReferenceEnabled,
  shouldCheckImpact,
  hasHierarchyImpact = false,
  checkImpact,
  setPendingAction,
  showWarningDialog,
  showBasicConfirmDialog,
}: {
  action: Action;
  objectReferenceEnabled: boolean;
  shouldCheckImpact: boolean;
  hasHierarchyImpact?: boolean;
  checkImpact: () => Promise<boolean>;
  setPendingAction: (action: Action) => void;
  showWarningDialog: () => void;
  showBasicConfirmDialog: () => void;
}): Promise<boolean> => {
  if (!objectReferenceEnabled || !shouldCheckImpact) {
    return false;
  }
  try {
    const hasObjectReferenceImpact = await checkImpact();
    setPendingAction(action);
    if (hasObjectReferenceImpact || hasHierarchyImpact) {
      showWarningDialog();
    } else {
      showBasicConfirmDialog();
    }
  } catch (_error) {
    setPendingAction(action);
    showBasicConfirmDialog();
  }
  return true;
};

export const checkObjectReferenceImpact = async ({
  tenantIdOrSlug,
  objectType,
  objectId,
}: {
  tenantIdOrSlug: string;
  objectType: string;
  objectId: string;
}): Promise<boolean> => {
  const response = await axios.get<ObjectReferenceArchiveImpact>(
    endpoints.v2_tenants_id_mdm_objects_object_type_object_id_archive_impact(
      tenantIdOrSlug,
      objectType,
      objectId
    )
  );
  return (
    (response.data?.reference_count ?? 0) > 0 ||
    (response.data?.referencing_profile_count ?? 0) > 0
  );
};

export const getObjectReferenceAttributeFilters = (
  objectType?: string
): string[] =>
  objectType ? OBJECT_REFERENCE_DEFAULT_PREVIEW_NAMES[objectType] ?? [] : [];

export const getDefaultPreviewAttributesForObjectType = (
  attributes: PreviewAttributeLike[],
  objectType?: string
): Map<string, string> => {
  const defaults = objectType
    ? OBJECT_REFERENCE_DEFAULT_PREVIEW_NAMES[objectType] ?? []
    : [];
  if (defaults.length === 0) {
    return new Map();
  }

  const attributeByName = new Map<string, PreviewAttributeLike>(
    attributes.map((attr) => [
      String(attr.name ?? attr.display_name ?? ""),
      attr,
    ])
  );

  return new Map(
    defaults
      .map((defaultName) => attributeByName.get(defaultName))
      .filter((attr): attr is PreviewAttributeLike & { id: string } =>
        Boolean(attr?.id)
      )
      .map((attr) => [getPreviewAttributeLabel(attr), attr.id as string])
  );
};

export const normalizeTemplateEntityType = (
  templateEntityType?: string
): string => {
  const normalized = (templateEntityType ?? "").toLowerCase();
  if (normalized.includes("product")) return "product";
  if (normalized.includes("supplier")) return "supplier";
  if (normalized.includes("customer")) return "customer";
  if (normalized.includes("distributor")) return "distributor";
  return "";
};

export const shouldIncludeObjectReferenceByTemplateEntity = (
  attribute: Pick<PreviewAttributeLike, "input_type" | "source_object_type">,
  templateEntityType?: string
): boolean => {
  if (attribute.input_type !== "object_reference") {
    return true;
  }
  const normalizedTemplateType =
    normalizeTemplateEntityType(templateEntityType);
  if (!normalizedTemplateType) {
    return true;
  }
  return attribute.source_object_type === normalizedTemplateType;
};

export const isProductTargetObjectReference = (
  attribute?: Pick<PreviewAttributeLike, "input_type" | "target_object_type">
): boolean =>
  attribute?.input_type === "object_reference" &&
  attribute?.target_object_type === "product";

export const getObjectReferencePermissionFlags = ({
  hasPermission,
  permissionSet,
}: {
  hasPermission: (permission: Permission) => boolean;
  permissionSet?: Record<string, Permission>;
}): { canView: boolean; canModify: boolean; canPublish: boolean } => {
  if (!permissionSet) {
    return {
      canView: true,
      canModify: true,
      canPublish: true,
    };
  }
  return {
    canView: hasPermission(permissionSet.view),
    canModify: hasPermission(permissionSet.modify),
    canPublish: hasPermission(permissionSet.publish),
  };
};

export const shouldShowObjectReferenceValueForUser = ({
  valueStatus,
  permissionSet,
  hasPermission,
}: {
  valueStatus?: string | null;
  permissionSet?: Record<string, Permission>;
  hasPermission: (permission: Permission) => boolean;
}): boolean => {
  const { canView, canModify, canPublish } = getObjectReferencePermissionFlags({
    permissionSet,
    hasPermission,
  });
  if (!canView) {
    return false;
  }
  if (!canModify && !canPublish) {
    return String(valueStatus ?? "").toLowerCase() === "published";
  }
  return true;
};

export function buildObjectRefPreviewDisplayRows(
  objectRef: ObjectReferenceResult,
  t: TFunction | ((key: string) => string)
): { label: string; value: string }[] {
  const configured = objectRef.preview_attributes ?? [];
  if (configured.length > 0) {
    return configured.map(({ key, value }) => ({
      label: key,
      value: String(value ?? ""),
    }));
  }
  const rows: { label: string; value: string }[] = [];
  if (objectRef.name) {
    rows.push({ label: t("Name"), value: objectRef.name });
  }
  if (objectRef.object_number) {
    rows.push({ label: t("ID"), value: objectRef.object_number });
  }
  return rows;
}

export function getObjectReferenceDisplayHref(
  ref: ObjectReferenceResult,
  options: ObjectReferencePathOptions
): string | null {
  const { isPortfolio, storePath, adminPath } = options;
  if (ref.object_type === "product") {
    if (isPortfolio) {
      return ref.slug ? `${storePath}/product/${ref.slug}` : null;
    }
    return `${adminPath}/pim/products/${ref.object_number ?? ref.id}`;
  }
  if (isPortfolio) {
    return null;
  }
  return getObjectReferenceDetailPath(adminPath, ref.object_type, ref.id);
}

const passesReferencePublishPermission = (
  status: string | null | undefined,
  objectType: string,
  pimProductType: PIMProduct["pim_product_type"] | null | undefined,
  context: ReferenceDisplayAccessContext
): boolean => {
  if (!context.hasPermission) {
    return true;
  }

  return shouldShowObjectReferenceValueForUser({
    valueStatus: status,
    permissionSet: getReferencePermissionSet(objectType, pimProductType),
    hasPermission: context.hasPermission,
  });
};

export function isObjectReferenceDisplayAccessible(
  ref: ObjectReferenceResult,
  context: ReferenceDisplayAccessContext
): boolean {
  const { isPortfolio, user, roleIsSellerAdmin } = context;

  if (ref.object_type === "product") {
    if (
      !isProductLikeReferenceDisplayAccessible(ref, {
        isPortfolio,
        user,
        roleIsSellerAdmin,
      })
    ) {
      return false;
    }

    if (isPortfolio || !user) {
      return ref.status === "published";
    }
  }

  const objectType = ref.object_type ?? context.targetObjectType ?? "";
  return passesReferencePublishPermission(
    ref.status,
    objectType,
    ref.pim_product_type,
    context
  );
}

export function isProductReferenceValueDisplayAccessible(
  ref: ProductLikeReferenceAccessFields & { status?: string | null },
  context: ReferenceDisplayAccessContext
): boolean {
  if (!isProductLikeReferenceDisplayAccessible(ref, context)) {
    return false;
  }

  return passesReferencePublishPermission(
    ref.status,
    "product",
    ref.pim_product_type,
    context
  );
}

type ReferenceRestrictedMessageInput = {
  status?: string | null;
  object_type?: string | null;
};

export function getReferenceRestrictedMessage(
  reference: ReferenceRestrictedMessageInput,
  t: TFunction
): string {
  const isProduct = (reference.object_type ?? "product") === "product";
  if (reference.status !== "published") {
    return isProduct
      ? t("Product informations is unavailable to view")
      : t("Profile information is unavailable to view");
  }
  return isProduct
    ? t("Product informations is not accessible")
    : t("Profile information is not accessible");
}

const toNonEmptyString = (value: unknown): string | null => {
  if (value === undefined || value === null) {
    return null;
  }
  const normalized = String(value).trim();
  return normalized.length > 0 ? normalized : null;
};

const getObjectReferenceIdFromUnknown = (value: unknown): string | null => {
  if (typeof value === "string") {
    return toNonEmptyString(value);
  }

  if (!value || typeof value !== "object") {
    return null;
  }

  if ("value" in value) {
    const fromValue = toNonEmptyString((value as { value?: unknown }).value);
    if (fromValue) {
      return fromValue;
    }
  }
  if ("id" in value) {
    return toNonEmptyString((value as { id?: unknown }).id);
  }

  return null;
};

export const getObjectReferenceIdsFromFormValue = (
  value: unknown
): string[] => {
  if (value === undefined || value === null || value === "") {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .map(getObjectReferenceIdFromUnknown)
      .filter((id): id is string => Boolean(id));
  }

  const singleId = getObjectReferenceIdFromUnknown(value);
  if (singleId) {
    return [singleId];
  }

  return [];
};

export const getObjectReferenceIdsFromAttributeValues = (
  existingValue?: AttributeValue | AttributeValue[]
): string[] =>
  (Array.isArray(existingValue)
    ? existingValue
    : existingValue
    ? [existingValue]
    : []
  )
    .map(
      (value) =>
        value.object_ref?.id ??
        (typeof value.value === "string" ? value.value : undefined)
    )
    .filter((id): id is string => Boolean(id));

export const shouldConfirmObjectReferenceChange = ({
  existingValue,
  nextFormValue,
}: {
  existingValue?: AttributeValue | AttributeValue[];
  nextFormValue: unknown;
}): boolean => {
  const existingIds = getObjectReferenceIdsFromAttributeValues(existingValue);
  if (existingIds.length === 0) {
    return false;
  }
  const nextIds = getObjectReferenceIdsFromFormValue(nextFormValue);
  const nextIdSet = new Set(nextIds);
  return existingIds.some((existingId) => !nextIdSet.has(existingId));
};

type RelatedEntitiesGroup = RelatedEntitiesResponse["groups"][number];

export const getRelatedEntitiesSearchPlaceholder = (
  group: RelatedEntitiesGroup,
  t: TFunction
): string => {
  const previewLabels = (group.source_preview_attributes ?? [])
    .map((item) => item.display_name || item.name)
    .filter((label): label is string => Boolean(label))
    .slice(0, 3);

  return previewLabels.length > 0
    ? `${t("Search by")} ${previewLabels.join(", ")}`
    : t("Search by name or ID...");
};
